import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Auth } from 'aws-amplify';
import useEffectiveHostId from '../../hooks/useEffectiveHostId';
import {
    LuClipboardList, LuCircleAlert, LuRefreshCw, LuCircleCheck,
    LuSearch, LuChevronRight, LuTriangleAlert, LuX, LuCheck, LuPartyPopper
} from 'react-icons/lu';
import './Housekeeping.css';
import { fetchTasks, createTask, updateTask, deleteTask, uploadTaskAttachment, getAttachmentViewUrl } from './services/taskService';
import { fetchSettings, saveSettings } from './services/settingsService';
import { fetchHostTaskPropertyOptions } from './services/hostTaskPropertyService';
import { fetchTeamMembers, fetchMemberships, inviteTeamMember } from './services/teamService';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
    PieChart, Pie, Cell 
} from 'recharts';

const DEFAULT_FILTERS = {
    property: 'All properties',
    status: 'All statuses',
    assignee: 'Anyone',
    date: 'Any date',
    priority: 'Any priority',
    search: '',
};

const DEFAULT_NEW_TASK = {
    title: '',
    description: '',
    property: '',
    property_id: '',
    bookingRef: '',
    type: 'Cleaning',
    assignee: '',
    dueDate: '',
    priority: 'Medium',
    attachments: null,
};

const getTodayString = () => new Date().toISOString().split('T')[0];

const AttachmentThumb = ({ attachment }) => {
    const [url, setUrl] = React.useState(null);

    React.useEffect(() => {
        if (attachment instanceof File) {
            const objectUrl = URL.createObjectURL(attachment);
            setUrl(objectUrl);
            return () => URL.revokeObjectURL(objectUrl);
        } else {
            getAttachmentViewUrl(attachment).then(setUrl).catch(() => {});
        }
    }, [attachment]);

    if (!url) return <div className="attachment-thumb attachment-loading" />;

    const name = attachment instanceof File ? attachment.name : attachment.split('/').pop();
    const isPdf = name.endsWith('.pdf');

    return (
        <a href={url} target="_blank" rel="noreferrer" className="attachment-thumb">
            {isPdf ? <div className="attachment-pdf-icon">PDF</div> : <img src={url} alt={name} />}
        </a>
    );
};


AttachmentThumb.propTypes = {
    attachment: PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.string]).isRequired,
};

const isTaskOverdue = (task, todayStr) => (
    Boolean(task?.dueDate) &&
    task.dueDate < todayStr &&
    task.status !== 'Completed' &&
    task.status !== 'Cancelled'
);

const normalizeTaskStatus = (task, todayStr) => {
    if (isTaskOverdue(task, todayStr)) {
        return { ...task, status: 'Overdue' };
    }

    return task;
};

const matchesFilterSelection = (selectedValue, defaultValue, taskValue) => (
    selectedValue === defaultValue || taskValue === selectedValue
);

const matchesSearchFields = (task, searchTerm, searchFields) => {
    if (!searchTerm) {
        return true;
    }

    const searchLower = searchTerm.toLowerCase();
    return searchFields.some((field) => String(task?.[field] || '').toLowerCase().includes(searchLower));
};

const matchesDateFilter = (task, dateFilter) => {
    if (dateFilter === DEFAULT_FILTERS.date) {
        return true;
    }

    if (dateFilter === 'Today') {
        return task.dueDate === getTodayString();
    }

    if (dateFilter === 'This Week' && task.dueDate) {
        const taskDate = new Date(task.dueDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);

        return taskDate >= today && taskDate <= nextWeek;
    }

    return true;
};

const matchesTaskFilters = (
    task,
    filters,
    {
        includeAssignee = false,
        includeDate = false,
        excludeLegacy = false,
        excludeCompleted = false,
        searchFields = ['title'],
    } = {}
) => {
    if (excludeLegacy && task.isLegacy) {
        return false;
    }

    if (excludeCompleted && task.status === 'Completed') {
        return false;
    }

    if (!matchesFilterSelection(filters.property, DEFAULT_FILTERS.property, task.property)) {
        return false;
    }

    if (!matchesFilterSelection(filters.status, DEFAULT_FILTERS.status, task.status)) {
        return false;
    }

    if (includeAssignee && !matchesFilterSelection(filters.assignee, DEFAULT_FILTERS.assignee, task.assignee)) {
        return false;
    }

    if (!matchesFilterSelection(filters.priority, DEFAULT_FILTERS.priority, task.priority)) {
        return false;
    }

    if (!matchesSearchFields(task, filters.search, searchFields)) {
        return false;
    }

    if (!includeDate) {
        return true;
    }

    return matchesDateFilter(task, filters.date);
};

const HostPropertyCare = () => {
    const { effectiveHostId, managedHostId, isPurelyPOM } = useEffectiveHostId();

    const [taskContext, setTaskContext] = useState('own');
    const asHostId = taskContext === 'managed' ? managedHostId : null;
    const loadRequestRef = useRef(0);

    const [activeTab, setActiveTab] = useState('Overview');
    const [tasks, setTasks] = useState([]);
    const [stats, setStats] = useState({ total: 0, overdue: 0, overdueIncrease: 0, inProgress: 0, completedToday: 0 });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'dueDate', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);

    const [newTask, setNewTask] = useState({ ...DEFAULT_NEW_TASK });

    const [viewingTask, setViewingTask] = useState(null);
    const [editedTask, setEditedTask] = useState(null);  

    const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });

    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false, title: '', message: '', confirmText: 'Confirm', cancelText: 'Cancel', onConfirm: null
    });

    const [currentUser, setCurrentUser] = useState({ name: '', email: '', group: '' });
    const [openDropdown, setOpenDropdown] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamMemberships, setTeamMemberships] = useState([]);
    const [showTeamInviteModal, setShowTeamInviteModal] = useState(false);
    const [teamInviteEmail, setTeamInviteEmail] = useState('');
    const [teamInviteError, setTeamInviteError] = useState('');
    const [teamInviteSent, setTeamInviteSent] = useState(false);

    useEffect(() => {
        Auth.currentAuthenticatedUser()
            .then(u => {
                const attrs = u.attributes || {};
                setCurrentUser({
                    name: attrs.given_name || attrs.name || u.username || '',
                    email: attrs.email || '',
                    group: attrs['custom:group'] || '',
                });
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        fetchTeamMembers().then(setTeamMembers).catch(() => {});
        fetchMemberships().then(setTeamMemberships).catch(() => {});
    }, []);

    useEffect(() => {
        if (!openDropdown) return;
        const handler = () => setOpenDropdown(null);
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [openDropdown]);

    useEffect(() => {
        if (!isModalOpen) return;
        const handler = (e) => { if (e.key === 'Escape') handleCancelModal(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isModalOpen]);

    const STATUS_OPTIONS = [
        { value: 'Pending',     label: '● Pending',     cls: 'status-pending' },
        { value: 'In progress', label: '● In progress', cls: 'status-in-progress' },
        { value: 'Completed',   label: '● Completed',   cls: 'status-completed' },
        { value: 'Overdue',     label: '● Overdue',     cls: 'status-overdue' },
        { value: 'Cancelled',   label: '● Cancelled',   cls: 'status-cancelled' },
    ];
    const PRIORITY_OPTIONS = [
        { value: 'Low',    label: 'Low',    cls: 'priority-low' },
        { value: 'Medium', label: 'Medium', cls: 'priority-medium' },
        { value: 'High',   label: 'High',   cls: 'priority-high' },
        { value: 'Urgent', label: 'Urgent', cls: 'priority-urgent' },
    ];

    const [propertyOptions, setPropertyOptions] = useState([]);
    const [timeView, setTimeView] = useState('Weekly');

    const DEFAULT_SETTINGS = {
        notifEmailAssigned: true,
        notifEmailOverdue: true,
        notifEmailCompleted: true,
        notifSmsUrgent: false,
        notifInappEnabled: true,
        defaultPriority: 'Medium',
        defaultAssignee: 'Anyone',
        autoAssignCleaning: false,
        requirePhotoProof: false,
    };
    const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
    const [settingsDraft, setSettingsDraft] = useState({ ...DEFAULT_SETTINGS });
    const [settingsSaved, setSettingsSaved] = useState(false);

    const settingsChanged = JSON.stringify(settings) !== JSON.stringify(settingsDraft);

    const handleSettingChange = (key, value) => {
        setSettingsDraft(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        fetchSettings()
            .then(data => {
                setSettings(data);
                setSettingsDraft(data);
            })
            .catch(() => {});
    }, []);

    const handleSaveSettings = async () => {
        try {
            await saveSettings(settingsDraft);
            setSettings({ ...settingsDraft });
            setSettingsSaved(true);
            setTimeout(() => setSettingsSaved(false), 3000);
        } catch {
            setSettingsSaved(false);
        }
    };

    const handleCancelSettings = () => {
        setSettingsDraft({ ...settings });
    };

    const TEAM_MEMBERS = useMemo(() => {
        const rows = [];
        if (taskContext === 'managed' && managedHostId) {
            const membership = teamMemberships.find(m => m.host_id === managedHostId);
            const hostName = membership?.host_name || membership?.host_email || managedHostId;
            const hostEmail = membership?.host_email || managedHostId;
            rows.push({ name: hostName, role: 'Host', email: hostEmail, properties: 'All', status: 'Active' });
            if (currentUser.name) {
                rows.push({ name: currentUser.name, role: 'Property Operations Manager', email: currentUser.email, properties: 'All', status: 'Active' });
            }
        } else {
            if (currentUser.name) {
                rows.push({ name: currentUser.name, role: 'Host', email: currentUser.email, properties: 'All', status: 'Active' });
            }
            teamMembers
                .filter(m => m.status === 'active' && m.member_email !== currentUser.email)
                .forEach(m => rows.push({ name: m.member_name || m.member_email, role: m.role, email: m.member_email, properties: 'All', status: 'Active' }));
        }
        return rows;
    }, [currentUser, teamMembers, teamMemberships, taskContext, managedHostId]);

    const INTEGRATIONS = [
        { name: 'Airbnb', logo: '🏠', connected: false },
        { name: 'Booking.com', logo: '🔵', connected: false },
        { name: 'Vrbo', logo: '🏡', connected: false },
    ];

    const reportData = useMemo(() => {
        const filtered = tasks.filter((task) => matchesTaskFilters(task, filters, {
            includeAssignee: true,
            includeDate: true,
            excludeLegacy: true,
        }));

        const total = filtered.length;
        const completed = filtered.filter(t => t.status === 'Completed').length;
        const overdue = filtered.filter(t => t.status === 'Overdue').length;
        const inProgress = filtered.filter(t => t.status === 'In progress').length;
        const pending = filtered.filter(t => t.status === 'Pending').length;
        const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

        const completedWithDates = filtered.filter(t => t.status === 'Completed' && t.completed_date && t.created_at);
        const avgMs = completedWithDates.length > 0
            ? completedWithDates.reduce((sum, t) => sum + (Number(t.completed_date) - Number(t.created_at)), 0) / completedWithDates.length
            : 0;
        const avgCompletionTime = completedWithDates.length > 0
            ? `${Math.floor(avgMs / 3600000)}h ${Math.floor((avgMs % 3600000) / 60000)}m`
            : '—';

        const weekStart = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const overdueThisWeek = filtered.filter(t =>
            t.status === 'Overdue' && t.due_date && Number(t.due_date) >= weekStart
        ).length;

        const distributionData = [
            { name: 'Pending', value: pending, color: '#6c757d' },
            { name: 'In Progress', value: inProgress, color: '#0062cc' },
            { name: 'Completed', value: completed, color: '#1e7e34' },
            { name: 'Overdue', value: overdue, color: '#dc3545' },
        ];

        const getIntervalKey = (timestamp) => {
            if (!timestamp) return null;
            const date = new Date(Number(timestamp));
            if (timeView === 'Daily') {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
            const monday = new Date(date);
            monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return `${fmt(monday)} – ${fmt(sunday)}`;
        };

        const getSortTimestamp = (timestamp) => {
            if (!timestamp) return 0;
            const date = new Date(Number(timestamp));
            if (timeView === 'Daily') return date.getTime();
            const monday = new Date(date);
            monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
            monday.setHours(0, 0, 0, 0);
            return monday.getTime();
        };

        const timeMap = {};
        filtered.forEach(task => {
            const key = getIntervalKey(task.created_at);
            const sortTs = getSortTimestamp(task.created_at);
            if (!key) return;
            if (!timeMap[key]) timeMap[key] = { date: key, _sort: sortTs, pending: 0, progress: 0, completed: 0, overdue: 0 };
            if (task.status === 'Pending') timeMap[key].pending++;
            else if (task.status === 'In progress') timeMap[key].progress++;
            else if (task.status === 'Completed') timeMap[key].completed++;
            else if (task.status === 'Overdue') timeMap[key].overdue++;
        });
        const timeData = Object.values(timeMap).sort((a, b) => a._sort - b._sort);

        const propertyMap = {};
        filtered.forEach(task => {
            const label = task.property || task.property_snapshot_label || 'Unknown';
            if (!propertyMap[label]) propertyMap[label] = { label, completed: 0, inProgress: 0, overdue: 0, total: 0 };
            propertyMap[label].total++;
            if (task.status === 'Completed') propertyMap[label].completed++;
            else if (task.status === 'In progress') propertyMap[label].inProgress++;
            else if (task.status === 'Overdue') propertyMap[label].overdue++;
        });
        const byProperty = Object.values(propertyMap);

        return { total, completed, overdue, inProgress, pending, completionRate, avgCompletionTime, overdueThisWeek, distributionData, timeData, byProperty };
    }, [tasks, filters, timeView]);

    useEffect(() => {
        const hostIdForOptions = asHostId ?? effectiveHostId;
        fetchHostTaskPropertyOptions(hostIdForOptions).then(setPropertyOptions);
    }, [asHostId, effectiveHostId]);
    
    const handleToggleComplete = async (task) => {
        const now = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        const todayStr = new Date().toISOString().split('T')[0];

        const newStatus = task.status === 'Completed' ? 'Pending' : 'Completed';

        const updatedTask = {
            ...task,
            status: newStatus,
            completedAt: newStatus === 'Completed' ? todayStr : null,
            completed_date: newStatus === 'Completed' ? Date.now() : null,
            activities: [
                ...(task.activities || []),
                { id: Date.now(), user: currentUser.name, action: `marked task ${newStatus}`, timestamp: now }
            ]
        };

        setTasks(tasks.map(t => t.id === task.id ? updatedTask : t));

        try {
            await updateTask(task.id, { status: newStatus });
        } catch {
            setTasks(tasks.map(t => t.id === task.id ? task : t));
        }
    };

    useEffect(() => {
        loadData();
    }, [asHostId]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters, sortConfig, activeTab]);

    useEffect(() => {
        const today = new Date();
        const todayStr = getTodayString();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        const activeTasks = tasks.filter(t => !t.isLegacy);

        const newStats = {
            total: activeTasks.length,
            overdue: activeTasks.filter(t => t.status === 'Overdue' || isTaskOverdue(t, todayStr)).length,
            overdueIncrease: activeTasks.filter(t => t.dueDate === yesterdayStr && t.status !== 'Completed').length,
            inProgress: activeTasks.filter(t => t.status === 'In progress').length,
            completedToday: activeTasks.filter(t => t.status === 'Completed' && t.completedAt === todayStr).length
        };
        setStats(newStats);
    }, [tasks]);

    const loadData = async () => {
        const requestId = ++loadRequestRef.current;
        setIsLoading(true);
        try {
            const data = await fetchTasks({}, asHostId);
            if (requestId !== loadRequestRef.current) return;
            const todayStr = new Date().toISOString().split('T')[0];
            const processedTasks = data.map(task => {
                if (task.dueDate && task.dueDate < todayStr && task.status !== 'Completed' && task.status !== 'Cancelled') {
                    return { ...task, status: 'Overdue', priority: 'Urgent' };
                }
                return task;
            });
            setTasks(processedTasks);
        } catch {
            if (requestId !== loadRequestRef.current) return;
            setTasks([]);
        } finally {
            if (requestId === loadRequestRef.current) setIsLoading(false);
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();
        try {
            const now = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
            
            const taskPayload = { 
                ...newTask, 
                status: 'Pending',
                activities: [{
                    id: Date.now(),
                    user: 'User',
                    action: 'created the task',
                    timestamp: now
                }]
            }; 
            
            let attachmentUrls = [];
            if (newTask.attachments?.length > 0) {
                attachmentUrls = await Promise.all(newTask.attachments.map(uploadTaskAttachment));
            }

            let created = await createTask({ ...taskPayload, attachments: attachmentUrls });
            
            const todayStr = new Date().toISOString().split('T')[0];
            if (created.dueDate && created.dueDate < todayStr && created.status !== 'Completed' && created.status !== 'Cancelled') {
                created = { ...created, status: 'Overdue', priority: 'Urgent' };
            }

            setTasks([created, ...tasks]);
            setIsModalOpen(false);
            resetForm();
        } catch {
            alert("Error creating task");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewTask(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setNewTask(prev => ({
            ...prev,
            attachments: [...(prev.attachments || []), ...files],
        }));
    };

    const handleEditFileChange = (e) => {
        const files = Array.from(e.target.files);
        setEditedTask(prev => ({
            ...prev,
            attachments: [...(prev.attachments || []), ...files],
        }));
    };

    const handlePropertyChange = (e) => {
        const selected = createPropertyOptions.find(o => o.id === e.target.value);
        setNewTask(prev => ({
            ...prev,
            property_id: selected?.id || '',
            property: selected?.label || '',
        }));
    };

    const handleEditPropertyChange = (e) => {
        const selected = editPropertyOptions.find(o => o.id === e.target.value);
        setEditedTask(prev => ({
            ...prev,
            property_id: selected?.id || prev.property_id,
            property: selected?.label || '',
        }));
    };

    const resetForm = () => {
        setNewTask({ ...DEFAULT_NEW_TASK });
    };

    const handleCancelModal = () => {
        const hasUnsavedChanges = newTask.title || newTask.description || newTask.property || newTask.bookingRef || newTask.assignee || newTask.dueDate || newTask.priority !== 'Medium' || newTask.attachments;
        if (hasUnsavedChanges) {
            setConfirmDialog({
                isOpen: true,
                title: 'Discard unsaved changes?',
                message: 'You have unsaved changes in your new task. Are you sure you want to cancel and lose your progress?',
                confirmText: 'Discard Task',
                cancelText: 'Keep Editing',
                onConfirm: () => {
                    setIsModalOpen(false);
                    resetForm();
                    closeConfirmDialog();
                }
            });
        } else {
            setIsModalOpen(false);
            resetForm();
        }
    };

    const openTaskDetails = (task) => {
        setViewingTask(task);
        setEditedTask({ ...task }); 
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditedTask(prev => ({ ...prev, [name]: value }));
    };

    const closeTaskDetails = () => {
        const isEdited = JSON.stringify(viewingTask) !== JSON.stringify(editedTask);
        if (isEdited) {
            setConfirmDialog({
                isOpen: true,
                title: 'Discard edits?',
                message: 'You have unsaved changes. Are you sure you want to close without saving?',
                confirmText: 'Discard Changes',
                cancelText: 'Keep Editing',
                onConfirm: () => {
                    setViewingTask(null);
                    setEditedTask(null);
                    closeConfirmDialog();
                }
            });
        } else {
            setViewingTask(null);
            setEditedTask(null);
        }
    };

    const handleSaveChanges = async () => {
        const now = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        const newLogs = [];

        if (viewingTask.status !== editedTask.status) {
            newLogs.push({ id: Date.now() + 1, user: 'User', action: `changed status to ${editedTask.status}`, timestamp: now });
        }
        if (viewingTask.assignee !== editedTask.assignee) {
            newLogs.push({ id: Date.now() + 2, user: 'User', action: `reassigned task to ${editedTask.assignee}`, timestamp: now });
        }
        if (viewingTask.dueDate !== editedTask.dueDate) {
            newLogs.push({ id: Date.now() + 3, user: 'User', action: `changed due date to ${editedTask.dueDate}`, timestamp: now });
        }
        if (viewingTask.priority !== editedTask.priority) {
            newLogs.push({ id: Date.now() + 4, user: 'User', action: `changed priority to ${editedTask.priority}`, timestamp: now });
        }
        if (viewingTask.property !== editedTask.property) {
            newLogs.push({ id: Date.now() + 6, user: 'User', action: `moved task to ${editedTask.property}`, timestamp: now });
        }
        
        if (newLogs.length === 0 && JSON.stringify(viewingTask) !== JSON.stringify(editedTask)) {
            newLogs.push({ id: Date.now() + 5, user: 'User', action: `updated task details`, timestamp: now });
        }

        const updatedTask = {
            ...editedTask,
            completed_date: editedTask.status === 'Completed' && viewingTask.status !== 'Completed'
                ? Date.now()
                : editedTask.completed_date ?? null,
            activities: [...(editedTask.activities || []), ...newLogs]
        };

        const newFiles = (editedTask.attachments || []).filter(a => a instanceof File);
        let existingUrls = (editedTask.attachments || []).filter(a => typeof a === 'string');

        if (newFiles.length > 0) {
            const uploaded = await Promise.all(newFiles.map(uploadTaskAttachment));
            existingUrls = [...existingUrls, ...uploaded];
        }

        const finalTask = { ...updatedTask, attachments: existingUrls };

        setTasks(tasks.map(t => t.id === finalTask.id ? finalTask : t));
        setViewingTask(null);
        setEditedTask(null);

        try {
            await updateTask(finalTask.id, finalTask);
        } catch {
            setTasks(tasks.map(t => t.id === viewingTask.id ? viewingTask : t));
        }
    };

    const confirmDeleteTask = async (taskId) => {
        setTasks(prev => prev.map(t =>
            t.id === taskId ? { ...t, isLegacy: true } : t
        ));
        setViewingTask(null);
        setEditedTask(null);
        closeConfirmDialog();

        try {
            await deleteTask(taskId);
        } catch {
            setTasks(prev => prev.map(t =>
                t.id === taskId ? { ...t, isLegacy: false } : t
            ));
        }
    };

    const handleDeleteSingleTask = () => {
        setConfirmDialog({
            isOpen: true,
            title: 'Delete Task?',
            message: `Are you sure you want to delete "${viewingTask.title}"? It will be moved to your Legacy Tasks list.`,
            confirmText: 'Yes, Delete',
            cancelText: 'Cancel',
            onConfirm: () => confirmDeleteTask(viewingTask.id),
        });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleClearFilters = () => {
        setFilters({ ...DEFAULT_FILTERS });
    };
    const renderCommonFilters = () => (
        <>
            <select name="property" value={filters.property} onChange={handleFilterChange}>
                <option value="All properties">All properties</option>
                {filterPropertyOptions.map(label => (
                    <option key={label} value={label}>{label}</option>
                ))}
            </select>

            <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="All statuses">All statuses</option>
                <option value="Pending">Pending</option>
                <option value="In progress">In progress</option>
                <option value="Completed">Completed</option>
                <option value="Overdue">Overdue</option>
            </select>
        </>
    );

    const getFilteredTasks = () => {
        return tasks.filter((task) => matchesTaskFilters(task, filters, {
            includeAssignee: true,
            includeDate: true,
            excludeLegacy: true,
            excludeCompleted: activeTab === 'Overview',
            searchFields: ['title', 'property', 'assignee'],
        }));
    };

    const filteredTasks = getFilteredTasks();

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedTasks = (tasksToSort) => {
        return [...tasksToSort].sort((a, b) => {
            const modifier = sortConfig.direction === 'asc' ? 1 : -1;

            if (sortConfig.key === 'priority') {
                const priorityValues = { 'Urgent': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
                const aVal = priorityValues[a.priority] || 0;
                const bVal = priorityValues[b.priority] || 0;
                return (aVal - bVal) * modifier;
            }
            if (sortConfig.key === 'dueDate') {
                const aDate = a.dueDate ? new Date(a.dueDate).getTime() : new Date('9999-12-31').getTime();
                const bDate = b.dueDate ? new Date(b.dueDate).getTime() : new Date('9999-12-31').getTime();
                return (aDate - bDate) * modifier;
            }
            const aStr = (a[sortConfig.key] || '').toString().toLowerCase();
            const bStr = (b[sortConfig.key] || '').toString().toLowerCase();
            
            if (aStr < bStr) return -1 * modifier;
            if (aStr > bStr) return 1 * modifier;
            return 0;
        });
    };

    const displayedTasks = getSortedTasks(filteredTasks);
    const closeConfirmDialog = () => setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    const filterPropertyOptions = useMemo(() => {
        const labelSet = new Set(propertyOptions.map(o => o.label));

        tasks.forEach((task) => {
            const normalizedProperty = String(task?.property || "").trim();
            if (normalizedProperty) {
                labelSet.add(normalizedProperty);
            }
        });

        [newTask.property, editedTask?.property].forEach((value) => {
            const normalizedValue = String(value || "").trim();
            if (normalizedValue) {
                labelSet.add(normalizedValue);
            }
        });

        return [...labelSet];
    }, [editedTask?.property, propertyOptions, newTask.property, tasks]);

    const createPropertyOptions = useMemo(() => propertyOptions, [propertyOptions]);

    const propertyLabelMap = useMemo(() => {
        const map = {};
        propertyOptions.forEach(o => { map[o.id] = o.label; });
        return map;
    }, [propertyOptions]);

    const getPropertyLabel = (task) =>
        (task.property_id && propertyLabelMap[task.property_id]) || task.property || '';

    const assigneeOptions = useMemo(() => {
        const set = new Set(tasks.map(t => t.assignee).filter(Boolean));
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [tasks]);

    const editPropertyOptions = useMemo(() => {
        const currentLabel = String(editedTask?.property || "").trim();
        if (currentLabel && !propertyOptions.some(o => o.label === currentLabel)) {
            return [...propertyOptions, { id: editedTask?.property_id || "", label: currentLabel }];
        }
        return propertyOptions;
    }, [propertyOptions, editedTask?.property, editedTask?.property_id]);

    const ITEMS_PER_PAGE = 10;
    const totalPages = Math.ceil(displayedTasks.length / ITEMS_PER_PAGE) || 1;

    let paginatedTasks = [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    paginatedTasks = displayedTasks.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const handlePrevPage = () => setCurrentPage(p => Math.max(p - 1, 1));
    const handleNextPage = () => setCurrentPage(p => Math.min(p + 1, totalPages));

    const handleExportCSV = () => {
        const now = new Date();
        const dateStr = now.toLocaleDateString('en-GB').replaceAll('/', '-');
        const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).replaceAll(':', '-');
        const timeDisplay = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

        const lines = [];
        

        lines.push(
            'TASK REPORT SUMMARY',
            `Generated,${now.toLocaleDateString('en-GB')} ${timeDisplay}`,
            '',
            'KPI METRICS',
            `Completion Rate,${reportData.completionRate}%`,
            `Avg Completion Time,${reportData.avgCompletionTime}`,
            `Total Tasks,${reportData.total}`,
            `Completed,${reportData.completed}`,
            `Pending,${reportData.pending}`,
            `In Progress,${reportData.inProgress}`,
            `Overdue,${reportData.overdue}`,
            `Overdue This Week,${reportData.overdueThisWeek}`,
            '',
            'TASKS BY PROPERTY',
            'Property,Total,Completed,In Progress,Overdue'
        );

        reportData.byProperty.forEach(prop => {
            lines.push(`"${prop.label}",${prop.total},${prop.completed},${prop.inProgress},${prop.overdue}`);
        });

        lines.push(
            '',
            'TASK LIST',
            'Title,Status,Priority,Property,Assignee,Due Date'
        );
        const filtered = tasks.filter(t => matchesTaskFilters(t, filters, {
            includeAssignee: true,
            includeDate: true,
            excludeLegacy: true,
        }));
        filtered.forEach(t => {
            lines.push([
                `"${(t.title || '').replaceAll('"', '""')}"`,
                t.status || '',
                t.priority || '',
                `"${(t.property || '').replaceAll('"', '""')}"`,
                t.assignee || '',
                t.dueDate || '',
            ].join(','));
        });

        const csv = lines.join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tasks-report_${dateStr}_${timeStr}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const renderSettingsToggle = (key, label, disabled = false) => (
        <div key={key} className="settings-toggle-row">
            <span className="settings-toggle-label">{label}</span>
            <button
                className={`settings-toggle ${!disabled && settingsDraft[key] ? 'on' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && handleSettingChange(key, !settingsDraft[key])}
                aria-label={label}
                aria-disabled={disabled}
            >
                <span className="settings-toggle-knob" />
            </button>
        </div>
    );

    const renderSettingsView = () => (
        <div className="settings-container">
            <div className="settings-main-grid">
                <div className="settings-left-col">
                    <div className="settings-card settings-team-card">
                        <div className="settings-card-header">
                            <h3 className="settings-card-title">Team Members</h3>
                            {taskContext !== 'managed' && (
                                <button className="btn-primary-green" onClick={() => setShowTeamInviteModal(true)}>+ Invite Member</button>
                            )}
                        </div>
                        <table className="settings-team-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Role</th>
                                    <th>Email</th>
                                    <th>Properties</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {TEAM_MEMBERS.map(member => (
                                    <tr key={member.email}>
                                        <td><LuChevronRight className="settings-row-arrow" />{member.name}</td>
                                        <td>{member.role}</td>
                                        <td>{member.email}</td>
                                        <td>{member.properties}</td>
                                        <td>
                                            <span className={`settings-status-badge ${member.status === 'Active' ? 'active' : 'suspended'}`}>
                                                {member.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="settings-card">
                        <div className="settings-card-header">
                            <h3 className="settings-card-title">Integrations</h3>
                            <span className="settings-coming-soon">Coming soon</span>
                        </div>
                        <div className="settings-integrations-grid">
                            {INTEGRATIONS.map(integration => (
                                <div key={integration.name} className="settings-integration-card">
                                    <div className="settings-integration-top">
                                        <span className="settings-integration-logo">{integration.logo}</span>
                                        <span className="settings-integration-name">{integration.name}</span>
                                    </div>
                                    {integration.connected ? (
                                        <button className="settings-integration-btn disconnect">
                                            ✓ Disconnect
                                        </button>
                                    ) : (
                                        <span className="settings-integration-status disconnected">Not Connected</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="settings-right-col">
                    <div className="settings-card">
                        <div className="settings-card-header">
                            <h3 className="settings-card-title">Notifications</h3>
                            <span className="settings-coming-soon">Coming soon</span>
                        </div>
                        <p className="settings-card-subtitle">Customize your app preferences.</p>
                        <p className="settings-group-label">Email notifications</p>
                        {renderSettingsToggle('notifEmailAssigned', 'Task assigned to me', true)}
                        {renderSettingsToggle('notifEmailOverdue', 'Task overdue', true)}
                        {renderSettingsToggle('notifEmailCompleted', 'Task completed', true)}
                        <p className="settings-group-label">SMS notifications</p>
                        {renderSettingsToggle('notifSmsUrgent', 'Urgent tasks only', true)}
                        <p className="settings-group-label">In-App notifications</p>
                        {renderSettingsToggle('notifInappEnabled', 'Enable notifications', true)}
                    </div>

                    <div className="settings-card">
                        <h3 className="settings-card-title">Default Property Settings</h3>
                        <p className="settings-card-subtitle">Manage property-level preferences.</p>
                        <div className="settings-field" style={{ marginBottom: '14px' }}>
                            <label htmlFor="setting-default-priority">Default task priority</label>
                            <select id="setting-default-priority" value={settingsDraft.defaultPriority} onChange={e => handleSettingChange('defaultPriority', e.target.value)}>
                                <option>Low</option>
                                <option>Medium</option>
                                <option>High</option>
                                <option>Urgent</option>
                            </select>
                        </div>
                        <div className="settings-field" style={{ marginBottom: '16px' }}>
                            <label htmlFor="setting-default-assignee">Default assignee</label>
                            <select id="setting-default-assignee" value={settingsDraft.defaultAssignee} onChange={e => handleSettingChange('defaultAssignee', e.target.value)}>
                                <option>Anyone</option>
                                {currentUser.name && <option value={currentUser.name}>{currentUser.name}</option>}
                            </select>
                        </div>
                        {renderSettingsToggle('autoAssignCleaning', 'Auto-assign cleaning after checkout')}
                        {renderSettingsToggle('requirePhotoProof', 'Require photo proof for completed tasks')}
                    </div>
                </div>
            </div>

            <div className="settings-footer">
                {settingsSaved && <span className="settings-saved-msg"><LuCheck /> Settings saved successfully.</span>}
                <button className="settings-cancel-btn" onClick={handleCancelSettings} disabled={!settingsChanged}>Cancel</button>
                <button className="btn-primary-green" onClick={handleSaveSettings} disabled={!settingsChanged}>Save changes</button>
            </div>

            {showTeamInviteModal && (
                <div className="team-modal-overlay">
                    <dialog className="team-modal" open aria-modal="true" aria-labelledby="hk-invite-title">
                        <h3 id="hk-invite-title">Invite team member</h3>
                        {teamInviteSent ? (
                            <p className="team-invite-success">✓ Invitation sent to {teamInviteEmail}</p>
                        ) : (
                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                setTeamInviteError('');
                                try {
                                    const created = await inviteTeamMember(teamInviteEmail, 'Property Operations Manager');
                                    setTeamMembers(prev => [...prev, created]);
                                    setTeamInviteSent(true);
                                    setTimeout(() => {
                                        setTeamInviteSent(false);
                                        setTeamInviteEmail('');
                                        setShowTeamInviteModal(false);
                                    }, 2500);
                                } catch {
                                    setTeamInviteError('Failed to send invitation. Please try again.');
                                }
                            }}>
                                <label className="team-modal-label">
                                    <span>Email address</span>
                                    <input
                                        type="email"
                                        className="team-modal-input"
                                        placeholder="colleague@example.com"
                                        value={teamInviteEmail}
                                        onChange={(e) => setTeamInviteEmail(e.target.value)}
                                        required
                                    />
                                </label>
                                {teamInviteError && <p className="team-invite-error">{teamInviteError}</p>}
                                <div className="team-modal-actions">
                                    <button type="submit" className="team-invite-btn">Send invitation</button>
                                    <button type="button" className="team-cancel-btn" onClick={() => setShowTeamInviteModal(false)}>Cancel</button>
                                </div>
                            </form>
                        )}
                    </dialog>
                </div>
            )}
        </div>
    );

    const renderReportsView = () => {
        return (
            <div className="reports-container">
                <div className="reports-controls">
                    <div className="reports-filters">
                            {renderCommonFilters()}
                        <select name="priority" value={filters.priority} onChange={handleFilterChange}>
                            <option value="Any priority">Any priority</option>
                            <option value="Urgent">Urgent</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                        </select>
                        <select name="assignee" value={filters.assignee} onChange={handleFilterChange}>
                            <option value="Anyone">Anyone</option>
                            {assigneeOptions.map(a => (
                                <option key={a} value={a}>{a}</option>
                            ))}
                        </select>
                        <select name="date" value={filters.date} onChange={handleFilterChange}>
                            <option value="Any date">Any date</option>
                            <option value="Today">Today</option>
                            <option value="This Week">This Week</option>
                        </select>
                    </div>
                    <button className="btn-export-green" onClick={handleExportCSV}>↥ Export CSV</button>
                </div>

                <div className="reports-main-grid">
                    <div className="reports-left-col">
                        <div className="reports-kpi-row">
                            <div className="kpi-card-v2">
                                <span className="kpi-title">Completion Rate</span>
                                <span className="kpi-main-val green-text">{reportData.completionRate}%</span>
                                <span className="kpi-sub">Completed {reportData.completed} out of {reportData.total} tasks</span>
                            </div>
                            <div className="kpi-card-v2">
                                <span className="kpi-title">Avg Completion Time</span>
                                <span className="kpi-main-val">{reportData.avgCompletionTime}</span>
                                <span className="kpi-sub">Average time taken to complete each task.</span>
                            </div>
                        </div>
                    </div>
                    <div className="reports-right-col">
                        <div className="kpi-card-v2">
                            <span className="kpi-title">Overdue Tasks</span>
                            <span className="kpi-main-val red-text">{reportData.overdue}</span>
                            <span className="kpi-sub">{reportData.overdueThisWeek} this week · {reportData.overdue} tasks exceeded their deadline.</span>
                        </div>
                    </div>
                </div>

                <div className="reports-main-grid">
                    <div className="chart-box tasks-over-time">
                        <div className="chart-header" style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px'}}>
                            <h4 style={{margin:0}}>Tasks Over Time</h4>
                            <div className="chart-toggle" style={{display:'flex', gap:'8px'}}>
                                {['Weekly', 'Daily'].map(v => (
                                    <button
                                        key={v}
                                        onClick={() => setTimeView(v)}
                                        style={{
                                            padding: '4px 12px',
                                            borderRadius: '4px',
                                            border: '1px solid #ced4da',
                                            background: timeView === v ? '#28a745' : '#fff',
                                            color: timeView === v ? '#fff' : '#495057',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                        }}
                                    >{v}</button>
                                ))}
                            </div>
                        </div>
                        <div style={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <BarChart data={reportData.timeData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#888', fontSize: 12}} />
                                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#888', fontSize: 12}} />
                                    <Tooltip cursor={{fill: '#f5f5f5'}} />
                                    <Legend wrapperStyle={{fontSize: '12px', paddingTop: '12px'}} />
                                    <Bar dataKey="pending" name="Pending" stackId="a" fill="#6c757d" barSize={30} />
                                    <Bar dataKey="progress" name="In Progress" stackId="a" fill="#0062cc" />
                                    <Bar dataKey="completed" name="Completed" stackId="a" fill="#1e7e34" />
                                    <Bar dataKey="overdue" name="Overdue" stackId="a" fill="#dc3545" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="chart-box task-distribution">
                        <h4>Task Distribution</h4>
                        <div className="donut-wrapper">
                            <div className="donut-chart-area">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={reportData.distributionData.filter(d => d.value > 0)}
                                            innerRadius="45%"
                                            outerRadius="65%"
                                            paddingAngle={3}
                                            dataKey="value"
                                            cx="50%"
                                            cy="50%"
                                        >
                                            {reportData.distributionData.filter(d => d.value > 0).map((entry) => (
                                                <Cell key={`cell-${entry.name}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value, name) => [`${value} tasks`, name]} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="donut-legend">
                                {reportData.distributionData.map(d => (
                                    <div key={d.name} className="summary-item" style={{ border: 'none', padding: '5px 0' }}>
                                        <span className="dot" style={{ backgroundColor: d.color, marginRight: '8px' }}></span>
                                        <span style={{fontSize: '13px'}}>{d.name}</span>
                                        <span className="sum-val">{d.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="reports-bottom-row">
                    <div className="chart-box tasks-by-property">
                        <h4>Tasks by Property</h4>
                        <div className="property-list-v2">
                            {reportData.byProperty.length === 0 ? (
                                <p className="empty-state">No tasks match current filters.</p>
                            ) : reportData.byProperty.map(prop => {
                                const completedPct = prop.total > 0 ? (prop.completed / prop.total) * 100 : 0;
                                const progressPct = prop.total > 0 ? (prop.inProgress / prop.total) * 100 : 0;
                                const overduePct = prop.total > 0 ? (prop.overdue / prop.total) * 100 : 0;
                                return (
                                    <div key={prop.label} className="prop-row-v2">
                                        <div className="prop-name-cell">{prop.label}</div>
                                        <div className="prop-status-label">
                                            {prop.completed === prop.total && prop.total > 0 ? 'Completed' : 'In progress'}
                                        </div>
                                        <div className="prop-progress-wrapper">
                                            <div className="multi-progress">
                                                <div className="progress-segment p-done" style={{width: `${completedPct}%`}}></div>
                                                <div className="progress-segment p-progress" style={{width: `${progressPct}%`}}></div>
                                                <div className="progress-segment p-overdue" style={{width: `${overduePct}%`}}></div>
                                            </div>
                                        </div>
                                        <div className="prop-total-val">{prop.total}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="chart-box task-summary-panel">
                        <h4>Task Summary</h4>
                        <div className="summary-list">
                            <div className="summary-item"><span className="sum-icon"><LuClipboardList /></span> Total Tasks <span className="sum-val">{reportData.total}</span></div>
                            <div className="summary-item"><span className="dot dot-pending" style={{marginRight:'8px'}}></span> Pending <span className="sum-val">{reportData.pending}</span></div>
                            <div className="summary-item"><span className="dot dot-inprogress" style={{marginRight:'8px'}}></span> In Progress <span className="sum-val">{reportData.inProgress}</span></div>
                            <div className="summary-item"><span className="dot dot-completed" style={{marginRight:'8px'}}></span> Completed <span className="sum-val">{reportData.completed}</span></div>
                            <div className="summary-item"><span className="dot dot-overdue" style={{marginRight:'8px'}}></span> Overdue <span className="sum-val">{reportData.overdue}</span></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (isLoading) return <div className="loading">Loading...</div>;

        switch (activeTab) {
            case 'Overview':
            case 'All Tasks': 
                return renderTableView();
            case 'My Tasks':
                return renderMyTasksView();
            case 'Reports':
                return renderReportsView();
            case 'Settings':
                return renderSettingsView();
            default:
                return null;
        }
    };
    const renderMyTasksView = () => {
        const todayStr = new Date().toISOString().split('T')[0];

        let myTasks = tasks.filter(t => t.assignee === currentUser.name && !t.isLegacy);

        myTasks = myTasks.filter(task => {
            const matchProperty = filters.property === 'All properties' || task.property === filters.property;
            const matchStatus = filters.status === 'All statuses' || task.status === filters.status;
            const matchPriority = filters.priority === 'Any priority' || task.priority === filters.priority;
            const searchLower = filters.search.toLowerCase();
            const matchSearch = filters.search === '' || (task.title?.toLowerCase().includes(searchLower));
            return matchProperty && matchStatus && matchPriority && matchSearch;
        });

        const todayTasks = myTasks.filter(t => t.dueDate === todayStr && t.status !== 'Overdue');
        const overdueTasks = myTasks.filter(t => t.status === 'Overdue' || (t.dueDate && t.dueDate < todayStr && t.status !== 'Completed'));
        
        const upcomingTasks = myTasks.filter(t => t.dueDate && t.dueDate > todayStr)
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
            .map(t => ({ ...t, priority: t.priority || 'Low', status: t.status || 'Pending' }));

        const renderTaskRow = (task, isOverdueSection = false) => {
            const displayPriority = isOverdueSection ? 'Urgent' : (task.priority || 'Low');
            const displayStatus = isOverdueSection ? 'Overdue' : task.status;
            
            const isCompleted = task.status === 'Completed';
            let displayTime = `Due ${task.dueDate}`;
            if (isOverdueSection) {
                displayTime = 'Overdue';
            } else if (task.dueDate === todayStr) {
                displayTime = 'Today';
            }
            return (
                <button 
                    type="button"
                    key={task.id} 
                    className={`my-task-card ${isOverdueSection ? 'is-overdue-card' : ''}`} 
                    onClick={() => openTaskDetails(task)}
                    style={{ opacity: isCompleted ? 0.6 : 1 }}
                >
                    <div className="my-task-left">
                        <div className="my-task-icon"><LuClipboardList /></div>
                        <div className="my-task-info">
                            <h4>{task.title}</h4>
                            <span>{getPropertyLabel(task)}</span>
                        </div>
                    </div>
                    
                    <div className="my-task-middle">
                        <span className="my-task-time">
                            {displayTime}
                        </span>
                    </div>

                    <div className="my-task-right">
                        <span className={`badge-status ${displayStatus.toLowerCase().replace(' ', '-')}`}>
                            ● {displayStatus}
                        </span>
                        <span className={`badge-priority ${displayPriority.toLowerCase()}`}>
                            {displayPriority}
                        </span>
                        
                        {isOverdueSection ? (
                            <div className="overdue-action-text">⍉ Overdue</div>
                        ) : (
                            <input 
                                type="checkbox" 
                                className="my-task-checkbox" 
                                checked={task.status === 'Completed'}
                                onClick={(e) => e.stopPropagation()} 
                                onChange={() => handleToggleComplete(task)}
                            />
                        )}
                    </div>
                </button>
            );
        };

        return (
            <div className="my-tasks-container">
                <div className="my-tasks-section">
                    <div className="section-header-row">
                        <div className="section-title">
                            <h3>Today's Tasks</h3>
                            <span className="task-count">{todayTasks.length} Tasks</span>
                        </div>
                        <div className="my-tasks-filters">
                            {renderCommonFilters()}
                            <select name="priority" value={filters.priority} onChange={handleFilterChange}>
                                <option value="Any priority">Any priority</option>
                                <option value="Urgent">Urgent</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                            </select>
                            <div className="search-box small-search">
                                <input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Search tasks" />
                                <LuSearch aria-hidden="true" />
                            </div>
                        </div>
                    </div>
                    <div className="my-task-list">
                        {todayTasks.length > 0 ? todayTasks.map(t => renderTaskRow(t)) : <p className="empty-state">No tasks for today! <LuPartyPopper aria-hidden="true" /></p>}
                    </div>
                </div>

                <div className="my-tasks-section">
                    <div className="section-title">
                        <h3>Overdue</h3>
                        <span className="task-count">{overdueTasks.length} Task(s)</span>
                    </div>
                    <div className="my-task-list">
                        {overdueTasks.length > 0 ? overdueTasks.map(t => renderTaskRow(t, true)) : null}
                    </div>
                </div>

                <div className="my-tasks-section">
                    <div className="section-title">
                        <h3>Upcoming</h3>
                        <span className="task-count">{upcomingTasks.length} Task(s)</span>
                    </div>
                    <div className="my-task-list">
                        {upcomingTasks.length > 0 ? upcomingTasks.map(t => renderTaskRow(t)) : null}
                    </div>
                </div>
            </div>
        );
    };
    const getSortIcon = (columnKey, defaultIcon = '') => {
        if (sortConfig.key !== columnKey) return defaultIcon;
        return sortConfig.direction === 'asc' ? '▴' : '▾';
    };

    const renderPagination = () => (
        <div className="pagination">
            <button onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
        </div>
    );

    const renderTableView = () => (
        <div className="overview-container">
            <div className="filters-bar">
                <div className="filters-dropdowns">
                    {renderCommonFilters()}
                    <select name="priority" value={filters.priority} onChange={handleFilterChange}>
                        <option value="Any priority">Any priority</option>
                        <option value="Urgent">Urgent</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                    </select>
                    <button className="btn-clear-filters" onClick={handleClearFilters}>Clear filters</button>
                    <div className="search-box small-search">
                        <input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Search tasks" />
                        <LuSearch aria-hidden="true" />
                    </div>
                </div>

                <div className="active-filters-row">
                    <div className="status-tags">
                        <span className="status-tag"><span className="dot dot-pending"></span> Pending</span>
                        <span className="status-tag"><span className="dot dot-inprogress"></span> In progress</span>
                        <span className="status-tag"><span className="dot dot-completed"></span> Completed</span>
                        <span className="status-tag"><span className="dot dot-overdue"></span> Overdue</span>
                        <span className="status-tag"><span className="dot dot-cancelled"></span> Cancelled</span>
                    </div>
                </div>
            </div>

            <div className="table-container">
                <table className="tasks-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('title')} className="sortable-header">
                                Task {getSortIcon('title', '')}
                            </th>
                            <th onClick={() => handleSort('property')} className="sortable-header">
                                Property {getSortIcon('property', '▾')}
                            </th>
                            <th onClick={() => handleSort('type')} className="sortable-header">
                                Type {getSortIcon('type', '')}
                            </th>
                            <th onClick={() => handleSort('assignee')} className="sortable-header">
                                Assignee {getSortIcon('assignee', '')}
                            </th>
                            <th onClick={() => handleSort('dueDate')} className="sortable-header">
                                Due Date {getSortIcon('dueDate', '▾')}
                            </th>
                            <th onClick={() => handleSort('priority')} className="sortable-header">
                                Priority {getSortIcon('priority', '▾')}
                            </th>
                            <th onClick={() => handleSort('status')} className="sortable-header">
                                Status {getSortIcon('status', '▾')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedTasks.length === 0 ? (
                            <tr>
                                <td colSpan="7" style={{textAlign: 'center', padding: '30px', color: '#495057'}}>
                                    No tasks match your filters (or all are completed/deleted).
                                </td>
                            </tr>
                        ) : (
                            paginatedTasks.map(task => {
                                const isOverdue = task.status === 'Overdue' || isTaskOverdue(task, getTodayString());
                                const displayPriority = isOverdue ? 'Urgent' : (task.priority || 'Low');
                                const displayStatus = isOverdue ? 'Overdue' : task.status;

                                return (
                                <tr key={task.id} className={`clickable-row row-${displayStatus.toLowerCase().replace(' ', '-')}`} onClick={() => openTaskDetails(task)}>
                                    <td>
                                        <div className="task-title-cell" title={task.title}>
                                            <LuChevronRight className="task-arrow" />
                                            <div className="truncate-text">
                                                <strong>{task.title}</strong>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{getPropertyLabel(task)}</td>
                                    <td>{task.type}</td>
                                    <td>{task.assignee}</td>
                                    <td>{task.dueDate === new Date().toISOString().split('T')[0] ? 'Today' : task.dueDate}</td>
                                    <td>
                                        <span className={`badge-priority ${displayPriority.toLowerCase()}`}>
                                            {displayPriority}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge-status ${displayStatus.toLowerCase().replace(' ', '-')}`}>
                                            ● {displayStatus}
                                        </span>
                                    </td>
                                </tr>
                            )})
                        )}
                    </tbody>
                </table>
                {renderPagination()}
            </div>
        </div>
    );

    return (
        <main className="task-dashboard-v2">
            <div className="top-header">
                <h2>Tasks</h2>
                <div className="top-header-actions">
                    {managedHostId && !isPurelyPOM && (
                        <div className="task-context-toggle">
                            <button
                                className={`task-context-btn ${taskContext === 'own' ? 'active' : ''}`}
                                onClick={() => setTaskContext('own')}
                            >
                                My tasks
                            </button>
                            <button
                                className={`task-context-btn ${taskContext === 'managed' ? 'active' : ''}`}
                                onClick={() => setTaskContext('managed')}
                            >
                                Co-host tasks
                            </button>
                        </div>
                    )}
                    <button className="btn-create-green btn-create-desktop" onClick={() => setIsModalOpen(true)}>
                        + Create Task
                    </button>
                </div>
            </div>

            <button className="btn-create-fab" onClick={() => setIsModalOpen(true)} aria-label="Create Task">
                +
            </button>

            <div className="tabs-nav">
                {['Overview', 'My Tasks', 'All Tasks', 'Reports', 'Settings'].map(tab => (
                    <button 
                        key={tab} 
                        className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {activeTab === 'Overview' && (
                <div className="overview-stats-row">
                    <div className="overview-stat-card">
                        <div className="overview-stat-icon"><LuClipboardList /></div>
                        <div className="overview-stat-info">
                            <span className="overview-stat-label">Total Tasks</span>
                            <span className="overview-stat-value">{stats.total}</span>
                        </div>
                    </div>
                    <div className="overview-stat-card">
                        <div className="overview-stat-icon error-icon"><LuCircleAlert /></div>
                        <div className="overview-stat-info">
                            <span className="overview-stat-label">Overdue</span>
                            <span className="overview-stat-value">
                                {stats.overdue} 
                                {stats.overdueIncrease > 0 && (
                                    <small> (+{stats.overdueIncrease} since yesterday)</small>
                                )}
                            </span>
                        </div>
                    </div>
                    <div className="overview-stat-card">
                        <div className="overview-stat-icon info-icon"><LuRefreshCw /></div>
                        <div className="overview-stat-info">
                            <span className="overview-stat-label">In Progress</span>
                            <span className="overview-stat-value">{stats.inProgress}</span>
                        </div>
                    </div>
                    <div className="overview-stat-card">
                        <div className="overview-stat-icon success-icon"><LuCircleCheck /></div>
                        <div className="overview-stat-info">
                            <span className="overview-stat-label">Completed Today</span>
                            <span className="overview-stat-value">{stats.completedToday}</span>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area">
                {renderContent()}
            </div>
            
            {isModalOpen && (
                <>
                <button className="modal-backdrop" onClick={handleCancelModal} aria-label="Close modal" />
                <div className="modal-overlay">
                    <div className="modal-content-large">
                        <div className="modal-header">
                            <h3>Create Task</h3>
                            <button className="close-btn" onClick={handleCancelModal}><LuX /></button>
                        </div>
                        <form onSubmit={handleCreateTask}>
                            <div className="form-group">
                                <label htmlFor='task-title'>Title</label>
                                <input type="text" id='task-title' name="title" value={newTask.title} onChange={handleInputChange} placeholder="Repair broken patio light" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor='task-description'>Description</label>
                                <textarea id='task-description' name="description" value={newTask.description} onChange={handleInputChange} placeholder="Description here..." rows="3" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor='task-property'>Property</label>
                                <select id='task-property' name="property" value={newTask.property_id} onChange={handlePropertyChange} required>
                                    <option value="" disabled hidden>Select Property</option>
                                    {createPropertyOptions.map(o => (
                                        <option key={o.id} value={o.id}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor='task-booking-ref'>Booking Reference</label>
                                <input type="text" id='task-booking-ref' name="bookingRef" value={newTask.bookingRef} placeholder="Coming soon" disabled />
                            </div>
                            <div className="form-group">
                                <label htmlFor='task-type'>Type</label>
                                <div className="radio-group">
                                    <label><input type="radio" id='task-type-cleaning' name="type" value="Cleaning" checked={newTask.type === 'Cleaning'} onChange={handleInputChange} /> Cleaning</label>
                                    <label><input type="radio" id='task-type-maintenance' name="type" value="Maintenance" checked={newTask.type === 'Maintenance'} onChange={handleInputChange} /> Maintenance</label>
                                    <label><input type="radio" id='task-type-inspection' name="type" value="Inspection" checked={newTask.type === 'Inspection'} onChange={handleInputChange} /> Inspection</label>
                                    <label><input type="radio" id='task-type-administration' name="type" value="Administration" checked={newTask.type === 'Administration'} onChange={handleInputChange} /> Administration</label>
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor='task-assignee'>Assignee</label>
                                <select id='task-assignee' name="assignee" value={newTask.assignee} onChange={handleInputChange} required>
                                    <option value="" disabled hidden>Select Assignee</option>
                                    {currentUser.name && <option value={currentUser.name}>{currentUser.name}{currentUser.email ? ` (${currentUser.email})` : ''}</option>}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor='task-due-date'>Due Date</label>
                                <input type="date" id='task-due-date' name="dueDate" value={newTask.dueDate} min={getTodayString()} onChange={handleInputChange} onClick={(e) => e.target.showPicker?.()} required />
                            </div>
                            <div className="form-group">
                                <label htmlFor='task-priority'>Priority</label>
                                <select id='task-priority' name="priority" value={newTask.priority} onChange={handleInputChange} required>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                    <option value="Urgent">Urgent</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor='task-attachments'>Attachments (optional)</label>
                                <div className="custom-file-upload">
                                    <input type="file" id="file-upload" name="attachments" multiple accept="image/*,application/pdf" onChange={handleFileChange} />
                                    <label htmlFor="file-upload">
                                        <span className="upload-text">{newTask.attachments?.length > 0 ? `${newTask.attachments.length} file(s) selected` : 'Upload file...'}</span>
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto' }}>
                                <button type="button" className="btn-text" onClick={handleCancelModal}>Cancel</button>
                                <button type="submit" className="btn-create-green">Create Task</button>
                            </div>
                        </form>
                    </div>
                </div>
                </>
            )}

            {viewingTask && editedTask && (
                <>
                <button className="modal-backdrop" onClick={closeTaskDetails} aria-label="Close modal" />
                <div className="modal-overlay">
                    <div className="modal-content-large task-details-modal">
                        <div className="modal-header details-header">
                            <input
                                className="details-title-input"
                                name="title"
                                value={editedTask.title}
                                onChange={handleEditChange}
                                placeholder="Task title"
                            />
                            <button className="close-btn" onClick={closeTaskDetails}><LuX /></button>
                        </div>
                        
                        <div className="details-badges-row">
                            <div className="custom-badge-select-wrapper" role="none" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                                <button
                                    type="button"
                                    className={`badge-select status-${editedTask.status.toLowerCase().replace(' ', '-')}`}
                                    onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
                                >
                                    ● {editedTask.status}
                                </button>
                                {openDropdown === 'status' && (
                                    <div className="custom-badge-options">
                                        {STATUS_OPTIONS.map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className={`custom-badge-option ${opt.cls}`}
                                                onClick={() => {
                                                    handleEditChange({ target: { name: 'status', value: opt.value } });
                                                    setOpenDropdown(null);
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="custom-badge-select-wrapper" role="none" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                                <button
                                    type="button"
                                    className={`badge-select priority-${editedTask.priority.toLowerCase()}`}
                                    onClick={() => setOpenDropdown(openDropdown === 'priority' ? null : 'priority')}
                                >
                                    {editedTask.priority}
                                </button>
                                {openDropdown === 'priority' && (
                                    <div className="custom-badge-options">
                                        {PRIORITY_OPTIONS.map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className={`custom-badge-option ${opt.cls}`}
                                                onClick={() => {
                                                    handleEditChange({ target: { name: 'priority', value: opt.value } });
                                                    setOpenDropdown(null);
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <select name="property" value={editedTask.property_id || ''} onChange={handleEditPropertyChange} className="badge-select property-badge">
                                {editPropertyOptions.map((o) => (
                                    <option key={o.id} value={o.id}>{o.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="details-body">
                            <div className="form-group">
                                <label htmlFor='task-description'>Description</label>
                                <textarea id='task-description' name="description" value={editedTask.description || ''} onChange={handleEditChange} rows="3" placeholder="Enter description..." />
                            </div>

                            <div className="form-row-grid">
                                <div className="form-group">
                                    <label htmlFor='task-assignee'>Assignee</label>
                                    <select id='task-assignee' name="assignee" value={editedTask.assignee} onChange={handleEditChange}>
                                        {currentUser.name && <option value={currentUser.name}>{currentUser.name}</option>}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor='task-type'>Type</label>
                                    <select id='task-type' name="type" value={editedTask.type} onChange={handleEditChange}>
                                        <option value="Cleaning">Cleaning</option>
                                        <option value="Maintenance">Maintenance</option>
                                        <option value="Inspection">Inspection</option>
                                        <option value="Administration">Administration</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor='task-booking-ref'>Booking Reference</label>
                                    <input id='task-booking-ref' type="text" name="bookingRef" value={editedTask.bookingRef || ''} placeholder="Coming soon" disabled />
                                </div>
                                <div className="form-group">
                                    <label htmlFor='task-due-date'>Due Date</label>
                                    <input id='task-due-date' type="date" name="dueDate" value={editedTask.dueDate || ''} onChange={handleEditChange} onClick={(e) => e.target.showPicker?.()} />
                                </div>
                            </div>

                            <div className="form-group attachments-section">
                                <div className="attachments-header">
                                    <label htmlFor='task-attachments-edit'>Attachments (optional)</label>
                                    <span className="attachments-count">{(editedTask.attachments?.length || 0)} Attachments</span>
                                </div>
                                <div className="attachments-box">
                                    {(!editedTask.attachments || editedTask.attachments.length === 0) ? (
                                        <p className="no-attachments-text">No attachments yet.</p>
                                    ) : (
                                        <div className="attachments-grid">
                                            {editedTask.attachments.map((f) => (
                                                <AttachmentThumb key={f instanceof File ? f.name : f} attachment={f} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="custom-file-upload" style={{ marginTop: '8px' }}>
                                    <input type="file" id="task-attachments-edit" name="attachments" multiple accept="image/*,application/pdf" onChange={handleEditFileChange} />
                                    <label htmlFor="task-attachments-edit">
                                        <span className="upload-text">Upload file...</span>
                                    </label>
                                </div>
                            </div>

                            <div className="activity-section">
                                <div className="activity-header">
                                    <h4>Activity</h4>
                                    {editedTask.activities && editedTask.activities.length > 0 && (
                                        <span className="created-info">
                                            Created by {editedTask.activities[0].user} on {editedTask.activities[0].timestamp}
                                        </span>
                                    )}
                                </div>
                                <div className="activity-list">
                                    {(!editedTask.activities || editedTask.activities.length === 0) ? (
                                        <p className="no-attachments-text">No activity recorded yet.</p>
                                    ) : (
                                        [...editedTask.activities].reverse().map(activity => (
                                            <div className="activity-item" key={activity.id} style={{ alignItems: 'flex-start' }}>
                                                <div className="activity-avatar">U</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <p><strong>{activity.user}</strong> {activity.action}</p>
                                                    <span style={{ fontSize: '11px', color: '#adb5bd' }}>{activity.timestamp}</span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer details-footer">
                            <button className="btn-text" onClick={closeTaskDetails}>Cancel</button>
                            
                            {JSON.stringify(viewingTask) === JSON.stringify(editedTask) ? (
                                <button className="btn-danger" onClick={handleDeleteSingleTask}>Delete</button>
                            ) : (
                                <button className="btn-create-green" onClick={handleSaveChanges}>Save Changes</button>
                            )}

                        </div>
                    </div>
                </div>
                </>
            )}

            {confirmDialog.isOpen && (
                <div className="confirm-modal-overlay">
                    <div className="confirm-modal-content">
                        <div className="confirm-modal-icon"><LuTriangleAlert /></div>
                        <h3>{confirmDialog.title}</h3>
                        <p>{confirmDialog.message}</p>
                        <div className="confirm-modal-actions">
                            <button className="btn-text" onClick={closeConfirmDialog}>{confirmDialog.cancelText}</button>
                            <button className="btn-danger" onClick={confirmDialog.onConfirm}>{confirmDialog.confirmText}</button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};
export default HostPropertyCare;
